import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Avatar, HeaderBreadcrumbs, Page, Scrollbar } from '@/components';
import { SkeletonLoadingTable } from '@/components/skeleton';
import { TableHeadCustom, TableNoData } from '@/components/table';
import useSettings from '@/hooks/useSettings';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import useTable from '@/hooks/useTable';
import { PATH_USER } from '@/features/app';
import { providersPath } from '@/features/users/providers';
import { getProviders } from '@/features/users/shared/api';
import {
  getInvoiceDryRun,
  getInvoiceDryRunIssues,
  getInvoiceDryRunProviderInvoices,
} from '@/features/invoices/api';
import type { InvoiceDryRunProviderInvoicePreview } from '@/features/invoices/types';
import {
  InvoiceDryRunIssueLink,
  InvoiceDryRunStatusLabel,
} from '../components';
import { invoicesPath } from '../routes/paths';
import {
  formatDryRunCount,
  formatDryRunDateRange,
  formatDryRunDateTime,
  formatDryRunMoney,
  formatRequestedBy,
  INVOICE_DRY_RUN_ISSUES_TABLE_HEAD,
  INVOICE_DRY_RUN_PROVIDER_PREVIEWS_TABLE_HEAD,
} from '../utils';

type SummaryItemProps = {
  label: string;
  value: React.ReactNode;
};

function SummaryItem({ label, value }: SummaryItemProps) {
  return (
    <Card variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
        {value}
      </Typography>
    </Card>
  );
}

function formatDetails(details: unknown) {
  if (details === null || details === undefined || details === '') return '-';
  if (typeof details === 'string') return details;

  try {
    return JSON.stringify(details);
  } catch (_error) {
    return String(details);
  }
}

const getStringRecordValue = (
  source: Record<string, unknown>,
  keys: string[]
) => {
  const value = keys
    .map((key) => source[key])
    .find((item) => typeof item === 'string' && item.trim());

  return typeof value === 'string' ? value.trim() : '';
};

const hasProviderDisplayName = (
  provider: InvoiceDryRunProviderInvoicePreview['provider']
) => {
  const providerRecord = (provider ?? {}) as Record<string, unknown>;
  const firstName = getStringRecordValue(providerRecord, [
    'first_name',
    'firstName',
  ]);
  const lastName = getStringRecordValue(providerRecord, [
    'last_name',
    'lastName',
  ]);

  return Boolean(
    [firstName, lastName].filter(Boolean).join(' ').trim() ||
      getStringRecordValue(providerRecord, [
        'name',
        'display_name',
        'displayName',
        'full_name',
        'fullName',
        'email',
      ])
  );
};

const formatProviderName = (invoice: InvoiceDryRunProviderInvoicePreview) => {
  const { provider } = invoice;
  const providerRecord = (provider ?? {}) as Record<string, unknown>;
  const firstName = getStringRecordValue(providerRecord, [
    'first_name',
    'firstName',
  ]);
  const lastName = getStringRecordValue(providerRecord, [
    'last_name',
    'lastName',
  ]);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const namedValue =
    fullName ||
    getStringRecordValue(providerRecord, [
      'name',
      'display_name',
      'displayName',
      'full_name',
      'fullName',
      'email',
    ]);

  if (namedValue) return namedValue;
  if (invoice.provider_id) return `Provider ${invoice.provider_id}`;

  return 'Provider';
};

const resolveProviderForPreview = (
  invoice: InvoiceDryRunProviderInvoicePreview,
  providersById:
    | Record<string, InvoiceDryRunProviderInvoicePreview['provider']>
    | undefined
) => {
  const providerFromList = invoice.provider_id
    ? providersById?.[String(invoice.provider_id)]
    : undefined;

  if (hasProviderDisplayName(invoice.provider)) return invoice.provider;
  if (providerFromList) return providerFromList;

  return invoice.provider ?? null;
};

function ProviderAccountLink({
  invoice,
}: {
  invoice: InvoiceDryRunProviderInvoicePreview;
}) {
  const providerId = invoice.provider?.id ?? invoice.provider_id;

  if (!providerId) return <Typography variant="body2">-</Typography>;

  const providerName = formatProviderName(invoice);
  const profileImage = invoice.provider?.profile_image;

  return (
    <Box
      component={RouterLink}
      to={providersPath('edit', {
        userId: String(providerId),
        tabSid: 'details',
      })}
      onClick={(event) => event.stopPropagation()}
      sx={{ display: 'inline-flex', textDecoration: 'none' }}
    >
      <Chip
        size="small"
        clickable
        avatar={
          <Avatar alt={providerName} src={profileImage}>
            {providerName.charAt(0).toUpperCase()}
          </Avatar>
        }
        label={providerName}
        title={`Open provider ${providerId}`}
      />
    </Box>
  );
}

function IssuesTab({ dryRunId }: { dryRunId: string }) {
  const {
    dense,
    page,
    rowsPerPage,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
    setPage,
  } = useTable({ defaultRowsPerPage: 25 });
  const { errorMsg } = useSnackbarMsg();
  const [filterType, setFilterType] = useState('');
  const queryParams = {
    page: page + 1,
    limit: rowsPerPage,
    ...(filterType.trim() ? { type: filterType.trim() } : {}),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['invoiceDryRunIssues', dryRunId, queryParams],
    queryFn: () => getInvoiceDryRunIssues(dryRunId, queryParams),
    keepPreviousData: true,
    onError: (error) => {
      errorMsg(error, 'Something went wrong while fetching dry run issues');
    },
  });

  const issues = data?.data ?? [];
  const totalIssues = data?.meta.total ?? issues.length;

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterType(event.target.value);
    setPage(0);
  };

  if (isLoading) {
    return <SkeletonLoadingTable />;
  }

  return (
    <>
      <Grid container spacing={2} sx={{ py: 2.5, px: 3 }}>
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Type"
            value={filterType}
            onChange={handleTypeChange}
          />
        </Grid>
      </Grid>

      <Scrollbar>
        <TableContainer sx={{ minWidth: 1050, position: 'relative' }}>
          <Table size={dense ? 'small' : 'medium'}>
            <TableHeadCustom headLabel={INVOICE_DRY_RUN_ISSUES_TABLE_HEAD} />
            <TableBody>
              {issues.map((issue) => (
                <TableRow key={String(issue.id)} hover>
                  <TableCell align="left">{issue.type || '-'}</TableCell>
                  <TableCell align="left">{issue.description || '-'}</TableCell>
                  <TableCell align="left" sx={{ maxWidth: 360 }}>
                    <Typography
                      variant="body2"
                      noWrap
                      title={formatDetails(issue.details)}
                    >
                      {formatDetails(issue.details)}
                    </Typography>
                  </TableCell>
                  <TableCell align="left">{issue.provider_id ?? '-'}</TableCell>
                  <TableCell align="left">{issue.booking_id ?? '-'}</TableCell>
                  <TableCell align="left">{issue.session_id ?? '-'}</TableCell>
                  <TableCell align="left">
                    <InvoiceDryRunIssueLink
                      link={issue.link}
                      bookingId={issue.booking_id}
                      sessionId={issue.session_id}
                      providerId={issue.provider_id}
                    />
                  </TableCell>
                </TableRow>
              ))}

              <TableNoData
                isNotFound={!issues.length}
                title="No issues found"
              />
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>

      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100, 150]}
          component="div"
          count={totalIssues}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />

        <FormControlLabel
          control={<Switch checked={dense} onChange={onChangeDense} />}
          label="Dense"
          sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
        />
      </Box>
    </>
  );
}

function ProviderInvoicePreviewsTab({ dryRunId }: { dryRunId: string }) {
  const {
    dense,
    page,
    rowsPerPage,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
    setPage,
  } = useTable({ defaultRowsPerPage: 25 });
  const { errorMsg } = useSnackbarMsg();
  const navigate = useNavigate();
  const [filterProviderId, setFilterProviderId] = useState('');
  const queryParams = {
    page: page + 1,
    limit: rowsPerPage,
    ...(filterProviderId.trim() ? { providerId: filterProviderId.trim() } : {}),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['invoiceDryRunProviderInvoices', dryRunId, queryParams],
    queryFn: () => getInvoiceDryRunProviderInvoices(dryRunId, queryParams),
    keepPreviousData: true,
    onError: (error) => {
      errorMsg(
        error,
        'Something went wrong while fetching provider invoice previews'
      );
    },
  });
  const { data: providers } = useQuery({
    queryKey: ['providers', 'list', 'all'],
    queryFn: () => getProviders(),
    onError: (error) => {
      errorMsg(error, 'Something went wrong while fetching providers');
    },
  });

  const invoices = data?.data ?? [];
  const totalInvoices = data?.meta.total ?? invoices.length;
  const providersById = providers?.reduce<
    Record<string, InvoiceDryRunProviderInvoicePreview['provider']>
  >((map, provider) => {
    map[String(provider.id)] = provider;
    return map;
  }, {});

  const handleProviderIdChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFilterProviderId(event.target.value);
    setPage(0);
  };

  if (isLoading) {
    return <SkeletonLoadingTable />;
  }

  return (
    <>
      <Grid container spacing={2} sx={{ py: 2.5, px: 3 }}>
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Provider ID"
            value={filterProviderId}
            onChange={handleProviderIdChange}
          />
        </Grid>
      </Grid>

      <Scrollbar>
        <TableContainer sx={{ minWidth: 950, position: 'relative' }}>
          <Table size={dense ? 'small' : 'medium'}>
            <TableHeadCustom
              headLabel={INVOICE_DRY_RUN_PROVIDER_PREVIEWS_TABLE_HEAD}
            />
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow
                  key={invoice.id}
                  hover
                  onClick={() =>
                    navigate(
                      invoicesPath('dryRunProviderInvoiceDetails', {
                        id: dryRunId,
                        invoiceId: String(invoice.id),
                      })
                    )
                  }
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell align="left">
                    <ProviderAccountLink
                      invoice={{
                        ...invoice,
                        provider: resolveProviderForPreview(
                          invoice,
                          providersById
                        ),
                      }}
                    />
                  </TableCell>
                  <TableCell align="left">
                    {formatDryRunDateRange(invoice.date_from, invoice.date_to)}
                  </TableCell>
                  <TableCell align="right">
                    {formatDryRunMoney(invoice.total)}
                  </TableCell>
                  <TableCell align="right">
                    {formatDryRunMoney(invoice.base_total)}
                  </TableCell>
                  <TableCell align="right">
                    {formatDryRunMoney(invoice.adjustments_total)}
                  </TableCell>
                  <TableCell align="right">
                    {formatDryRunMoney(invoice.adjusted_total)}
                  </TableCell>
                  <TableCell align="right">
                    {invoice.item_count ?? '-'}
                  </TableCell>
                </TableRow>
              ))}

              <TableNoData
                isNotFound={!invoices.length}
                title="No provider invoice previews found"
              />
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>

      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100, 150]}
          component="div"
          count={totalInvoices}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />

        <FormControlLabel
          control={<Switch checked={dense} onChange={onChangeDense} />}
          label="Dense"
          sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
        />
      </Box>
    </>
  );
}

export default function InvoiceDryRunDetails() {
  const { themeStretch } = useSettings();
  const { errorMsg } = useSnackbarMsg();
  const { id = '' } = useParams();
  const [currentTab, setCurrentTab] = useState('issues');

  const {
    data: dryRun,
    isError,
    isLoading,
  } = useQuery({
    queryKey: ['invoiceDryRun', id],
    queryFn: () => getInvoiceDryRun(id),
    enabled: !!id,
    onError: (error) => {
      errorMsg(error, 'Something went wrong while fetching dry run');
    },
  });

  return (
    <Page title="Invoice Dry Run Details">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`Invoice Dry Run #${id}`}
          links={[
            { name: 'Dashboard', href: PATH_USER.root },
            { name: 'Invoice Dry Runs', href: invoicesPath('dryRuns') },
            { name: `Dry Run #${id}`, href: '' },
          ]}
        />

        {isLoading ? (
          <SkeletonLoadingTable />
        ) : isError || !dryRun ? (
          <Alert severity="error">Unable to load invoice dry run.</Alert>
        ) : (
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryItem
                  label="Status"
                  value={<InvoiceDryRunStatusLabel status={dryRun.status} />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryItem
                  label="Date range"
                  value={formatDryRunDateRange(
                    dryRun.date_from,
                    dryRun.date_to
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryItem
                  label="Total jobs"
                  value={formatDryRunCount(dryRun.total_jobs, dryRun.status)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryItem
                  label="Processed jobs"
                  value={formatDryRunCount(
                    dryRun.processed_jobs,
                    dryRun.status
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryItem
                  label="Issue count"
                  value={formatDryRunCount(dryRun.issue_count, dryRun.status)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryItem
                  label="Provider invoice count"
                  value={formatDryRunCount(
                    dryRun.provider_invoice_count,
                    dryRun.status
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryItem
                  label="Requested by"
                  value={formatRequestedBy(dryRun.requested_by)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryItem
                  label="Created at"
                  value={formatDryRunDateTime(dryRun.created_at)}
                />
              </Grid>
            </Grid>

            {dryRun.status === 'FAILED' && dryRun.error_message && (
              <Alert severity="error">{dryRun.error_message}</Alert>
            )}

            <Card>
              <Tabs
                allowScrollButtonsMobile
                variant="scrollable"
                scrollButtons="auto"
                value={currentTab}
                onChange={(_event, value) => setCurrentTab(value)}
                sx={{ px: 2, bgcolor: 'background.neutral' }}
              >
                <Tab disableRipple label="Issues" value="issues" />
                <Tab
                  disableRipple
                  label="Provider Invoice Previews"
                  value="provider-invoices"
                />
              </Tabs>

              <Divider />

              {currentTab === 'issues' ? (
                <IssuesTab dryRunId={id} />
              ) : (
                <ProviderInvoicePreviewsTab dryRunId={id} />
              )}
            </Card>
          </Stack>
        )}
      </Container>
    </Page>
  );
}
